import {
  alpha,
  Box,
  Button,
  Chip,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { debounce, get, pickBy, some } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import * as Icon from 'react-feather';
import { Link, useNavigate } from 'react-router-dom';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FormikForm from 'src/components/common/FormikForm';

import { ArrowForward as ArrowForwardIcon, TravelExplore } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import dayjs from 'dayjs';
import { isArray } from 'lodash';
import { useTranslation } from 'react-i18next';
import iconAITag from 'src/asset/icons/ai-tag-icon.svg';
import IconFeather from 'src/components/common/Icon';
import DatePickerWithDefaultIcon from 'src/components/DatePicker';
import FormAutoCompleteByCountry from 'src/components/Form/FormAutoCompleteByCountry';
import FormSearchHsCode from 'src/components/Form/FormSearchHsCode';
import FormSearchHsCodeByAI from 'src/components/Form/FormSearchHsCodeByAI';
import CreditCostIndicator from 'src/components/common/CreditCostIndicator';
import { CreditCoinIcon } from 'src/components/Icons';
import CheckedCredit from 'src/components/Icons/CheckedCredit';
import { TypeTabTradeData } from 'src/features/TradeData/config';
import { useFeatureAccess } from 'src/hooks/useFeatureAccess';
import { useQueryParams } from 'src/hooks/useQueryParam';

import { useQueryGetSentencePrediction } from 'src/services/queries/useQueryAILabeling';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import {
  useQueryListTradeDataHSCode,
  useQueryTransactionsCount,
  useQueryTransactionsPaymentStatus,
} from 'src/services/queries/useQueryTradeData';
import { FORMAT_DATE_TIME, LIMIT_GET_LIST_BILLS_OF_LADING } from 'src/utils/constants/config';
import { COUNTRIES } from 'src/utils/constants/countries';

import { URL_ENDPOINT } from 'src/utils/constants/service';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { TRADE_DATA_URL_VIDEO } from 'src/utils/constants/videosUrl';
import { sortObjectsArrayByKey } from 'src/utils/helpers/arrayHelpers';
import { getTradeDataEndMonth } from 'src/utils/helpers/dayjs';
import { formatNumberWithSuffix } from 'src/utils/helpers/number';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import { getCountryByCountryCode, normalizeHSCodeToHS6, renderNumberOrDefault } from 'src/utils/helpers/stringHelpers';
import { TypeGlobalTradeDataFormSearch } from 'src/utils/typings/dto/tradeData';
import { useTradeDataSearchFormState } from 'src/zustand/useTradeDataSearchForm';
import * as Yup from 'yup';
import { FeatureAction } from 'src/utils/constants/featuresPoint';

interface IProps {
  handleClose: () => void;
}

export enum TradeDataSearchBy {
  HS_CODE = 'hs_code',
  HS_CODE_AI = 'hs_code_ai',
}

const FormSearchGlobalTrade = (props: IProps) => {
  const { handleClose } = props;
  const theme = useTheme();
  const {
    data: { data: runtimeConfig },
  } = useQueryRuntimeConfig();
  const navigate = useNavigate();
  const [visibleMoreFilter, setVisibleMoreFilter] = useState(false as boolean);
  const { update: updateTradeDataSearchFormStateStore } = useTradeDataSearchFormState();
  const params = useQueryParams();

  const [searchBy, setSearchBy] = useState<string>(params._search_by || TradeDataSearchBy.HS_CODE);

  const { t } = useTranslation();

  const getDefaultValue = (key: string) => {
    return get(params, key, '') as string;
  };

  const [initTradeFrom, initTradeTo] = useMemo(() => {
    return [
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_FROM, 'YYYY-MM-DD'),
      dayjs(runtimeConfig.DEFAULT_TRADE_DATA_TO, 'YYYY-MM-DD'),
    ];
  }, [runtimeConfig.DEFAULT_TRADE_DATA_FROM, runtimeConfig.DEFAULT_TRADE_DATA_TO]);

  const initHsCode = {
    code: params.hs_code || '',
  };

  const initAIHsCode = {
    hs_code: params.hs_code || '',
  };

  const formik = useFormik<TypeGlobalTradeDataFormSearch>({
    enableReinitialize: true,
    initialValues: {
      hs_code: initHsCode,
      hs_code_ai: initAIHsCode,
      buyer_keyword: getDefaultValue('buyer_keyword'),
      supplier_keyword: getDefaultValue('supplier_keyword'),
      import_country: getCountryByCountryCode(getDefaultValue('imp_country_code')),
      export_country: getCountryByCountryCode(getDefaultValue('exp_country_code')),
      port_of_origin: null,
      port_of_delivery: null,
      supplier: undefined,
      buyer: undefined,
      trade_from: getDefaultValue('start_date') ? dayjs(getDefaultValue('start_date'), 'YYYYMMDD') : initTradeFrom,
      trade_to: getDefaultValue('end_date') ? dayjs(getDefaultValue('end_date'), 'YYYYMMDD') : initTradeTo,
    },
    initialTouched: {
      trade_from: true,
      trade_to: true,
    },
    validateOnChange: true,
    validateOnBlur: false,
    validationSchema: Yup.object().shape({
      // hs_code: Yup.mixed()
      //   .nullable()
      //   .test('required', 'HS Code is required', function (value) {
      //     return searchBy === TradeDataSearchBy.HS_CODE ? !!value : true;
      //   }),

      trade_from: Yup.mixed()
        .required('Trade Date From is required')
        .test('is-greater', t('trade_data.common.field.error.date_range.invalid'), function (_, context) {
          const trade_from = dayjs(context.parent.trade_from || 'Invalid date');
          const trade_to = dayjs(context.parent.trade_to || 'Invalid date');
          if (!(trade_from.isValid() && trade_to.isValid())) return true;
          return trade_from.isBefore(trade_to);
        }),
      // .test('maximum-range', t('trade_data.common.field.error.date_range.exceed'), function (value, context) {
      //   const trade_from = dayjs(context.parent.trade_from || 'Invalid date');
      //   const trade_to = dayjs(context.parent.trade_to || 'Invalid date');
      //   if (!(trade_from.isValid() && trade_to.isValid())) return true;
      //   return trade_to.diff(trade_from, 'month') <= TRADE_DATA_MAXIMUM_SUPPORTED_DATE_RANGE_MONTHS;
      // })
      trade_to: Yup.mixed().required('Trade Date To is required'),
      export_country: Yup.mixed()
        .nullable()
        .test('not-duplicated', 'Export and import countries must be different', (_, context) => {
          const export_country = context.parent.export_country?.code;
          const import_country = context.parent.import_country?.code;

          if (!(export_country && import_country)) return true;

          return export_country !== import_country;
        }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const searchString =
        handleQueryParamFromObject(pickBy(bolRequestPayload, (value) => !(value === undefined || value === null))) +
        `&tab=${TypeTabTradeData.Buyers}`;

      setSubmitting(false);

      navigate({ pathname: AuthRouteKeys.TradeData, search: searchString });
      handleClose();
    },
  });
  useEffect(() => {
    updateTradeDataSearchFormStateStore(formik.values, true);
  }, [formik.values]);

  const [hsCodeSearchKeyword, setHSCodeSearchKeyword] = useState('');
  const [hsCodeAISearchKeyword, setHsCodeAISearchKeyword] = useState('');

  const bolRequestPayload = useMemo(() => {
    const values = formik.values;
    return {
      hs_code:
        searchBy === TradeDataSearchBy.HS_CODE
          ? values.hs_code?.code || ''
          : normalizeHSCodeToHS6(values.hs_code_ai?.hs_code),
      start_date: values.trade_from?.startOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
      buyer_keyword: values.buyer_keyword || undefined,
      supplier_keyword: values.supplier_keyword || undefined,
      end_date: getTradeDataEndMonth(values?.trade_to).format(FORMAT_DATE_TIME.yyyy_mm_dd),
      offset: 0,
      limit: LIMIT_GET_LIST_BILLS_OF_LADING,
      exp_country_code: values.export_country?.code,
      imp_country_code: values.import_country?.code,
      exp_port_code: values.port_of_origin?.port_code,
      imp_port_code: values.port_of_delivery?.port_code,
      supplier_id: values.supplier?.id || '',
      buyer_id: values.buyer?.id || '',
      _search_by: searchBy,
    };
  }, [formik.values]);

  const transactionsPaymentStatusResp = useQueryTransactionsPaymentStatus(
    formik.isValid && !formik.isValidating ? bolRequestPayload : null,
  );
  const transactionsCountResp = useQueryTransactionsCount(
    formik.isValid && !formik.isValidating ? bolRequestPayload : null,
  );

  const { getFeaturePoints, hasAccess } = useFeatureAccess();
  const isAccessBuyer = hasAccess([URL_ENDPOINT.tradeData.buyersTable]);
  const tradeDataEndpoint = isAccessBuyer
    ? URL_ENDPOINT.tradeData.buyersTable
    : URL_ENDPOINT.tradeData.suppliersTable;
  const pointsViewTradeData = getFeaturePoints(tradeDataEndpoint, FeatureAction.VIEW);

  const isTransactionQueryPaid = transactionsPaymentStatusResp.data?.data?.payment_status;

  const dataHSCodeOptionsResp = useQueryListTradeDataHSCode({
    country_code: formik.values.import_country?.code,
    keyword: hsCodeSearchKeyword || '',
    limit: 50,
  });

  const dataHSCodeAIOptionsResp = useQueryGetSentencePrediction({
    text: hsCodeAISearchKeyword,
  });

  useEffect(() => {
    if (
      formik.values.import_country?.code ||
      formik.values.export_country?.code ||
      formik.values.supplier_keyword ||
      formik.values.buyer_keyword
    ) {
      setVisibleMoreFilter(true);
    }
  }, [
    formik.values.supplier_keyword,
    formik.values.buyer_keyword,
    formik.values.import_country,
    formik.values.export_country,
  ]);

  const getError = (id: string) => {
    return get(formik.touched, id) && Boolean(get(formik.errors, id));
  };

  const renderError = (id: string) => {
    return (
      get(formik.touched, id) &&
      get(formik.errors, id) && (
        <FormHelperText sx={{ marginLeft: 2 }} error id="standard-weight-helper-text-email-login">
          {get(formik.errors, id, '').toString()}
        </FormHelperText>
      )
    );
  };
  //
  // useEffect(() => {
  //   if (!open) {
  //     setVisibleMoreFilter(false);
  //     formik.resetForm();
  //   }
  //   if (open && params['incorporation_date']) {
  //     setVisibleMoreFilter(true);
  //   }
  // }, [open]);

  const handleClickMoreFilter = () => {
    setVisibleMoreFilter(!visibleMoreFilter);
  };

  const renderButtonMoreFilter = () => {
    if (!visibleMoreFilter) {
      return (
        <Button onClick={handleClickMoreFilter} color="suggestion" size="small" variant="text">
          Advanced Search{' '}
          <IconFeather muiProps={{ color: 'inherit', fontSize: 'small' }} icon={<Icon.ChevronsDown size={24} />} />
        </Button>
      );
    } else {
      return (
        <Button onClick={handleClickMoreFilter} color="suggestion" size="small" variant="text">
          Basic Search{' '}
          <IconFeather muiProps={{ color: 'inherit', fontSize: 'small' }} icon={<Icon.ChevronsUp size={24} />} />
        </Button>
      );
    }
  };

  return (
    <Box>
      <FormikForm formik={formik}>
        <Box width={600}>
          <Typography mb={4} textAlign={'center'} color={theme.palette.text.secondary} variant="body1">
            {t('Explore import export data using HS code or product name.')}{' '}
            <Link to={TRADE_DATA_URL_VIDEO} target="_blank">
              <Typography color={'secondary'} textAlign={'center'} variant="caption">
                Learn more
              </Typography>
            </Link>
          </Typography>

          <Box>
            <FormLabel id="include_all_company_status">
              <Typography variant="caption2">Search By</Typography>
            </FormLabel>
            <RadioGroup>
              <Stack direction={'row'}>
                <FormControlLabel
                  control={
                    <Radio
                      checked={searchBy === TradeDataSearchBy.HS_CODE}
                      value={TradeDataSearchBy.HS_CODE}
                      onChange={(e) => {
                        setSearchBy(TradeDataSearchBy.HS_CODE);
                      }}
                      size={'small'}
                    />
                  }
                  label={<Typography variant="caption2">{'HS Code'}</Typography>}
                />
                <FormControlLabel
                  sx={{ mr: 0.5 }}
                  control={
                    <Radio
                      checked={searchBy === TradeDataSearchBy.HS_CODE_AI}
                      value={TradeDataSearchBy.HS_CODE_AI}
                      onChange={(e) => {
                        setSearchBy(TradeDataSearchBy.HS_CODE_AI);
                      }}
                      size={'small'}
                    />
                  }
                  label={
                    <Typography color={theme.palette.text.secondary} variant="caption2">
                      <Typography variant="caption2">{'Product Description'} </Typography>
                      <Tooltip
                        title={
                          'Enter a Product Description to search. Our AI will analyze your input and predict the most suitable HS Code for your product.'
                        }
                      >
                        <Chip
                          color="secondary"
                          label="AI Predicted"
                          size="small"
                          icon={<img src={iconAITag} />}
                          clickable={false}
                          sx={{
                            fontSize: theme.spacing(1),
                            '&:hover': {
                              color: theme.palette.secondary.main,
                              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                            },
                          }}
                        />
                      </Tooltip>
                    </Typography>
                  }
                />
              </Stack>
            </RadioGroup>
          </Box>

          <Box sx={{ mt: 4 }} />
          <Stack direction={'column'} spacing={2}>
            {searchBy === TradeDataSearchBy.HS_CODE_AI ? (
              <Box>
                <FormSearchHsCodeByAI
                  required={true}
                  id={'hs_code_ai'}
                  onBlur={() => {
                    formik.setFieldTouched('hs_code_ai');
                  }}
                  inputValue={hsCodeSearchKeyword || ''}
                  value={formik.values.hs_code_ai}
                  options={
                    isArray(dataHSCodeAIOptionsResp?.data?.data) && !dataHSCodeAIOptionsResp.isFetching
                      ? dataHSCodeAIOptionsResp?.data?.data || []
                      : []
                  }
                  onChange={(value: any) => {
                    // setHSCodeValue(value)
                    formik.setFieldValue('hs_code_ai', value);
                  }}
                  onTextInputChange={(value: string) => {
                    setHsCodeAISearchKeyword(value);
                  }}
                  loading={dataHSCodeAIOptionsResp?.isFetching}
                  isError={getError('hs_code_ai')}
                />
                {renderError('hs_code_ai')}
              </Box>
            ) : (
              <></>
            )}
            {searchBy === TradeDataSearchBy.HS_CODE ? (
              <Box>
                <FormSearchHsCode
                  required={true}
                  id={'hs_code'}
                  onBlur={() => {
                    formik.setFieldTouched('hs_code');
                  }}
                  inputValue={hsCodeSearchKeyword || ''}
                  value={formik.values.hs_code}
                  options={dataHSCodeOptionsResp.data?.data}
                  onChange={(value: any) => {
                    // setHSCodeValue(value)
                    formik.setFieldValue('hs_code', value);
                  }}
                  onTextInputChange={(value: string) => {
                    setHSCodeSearchKeyword(value);
                  }}
                  loading={dataHSCodeOptionsResp.isFetching}
                  isError={getError('hs_code')}
                />
                {renderError('hs_code')}
              </Box>
            ) : (
              <></>
            )}
            <Stack direction={'row'} spacing={2}>
              <Box flex={1}>
                <DatePickerWithDefaultIcon
                  disableFuture={true}
                  value={formik.values.trade_from}
                  minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                  maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                  // disabled={true}
                  onChange={(data) => {
                    formik.setFieldValue('trade_from', data);
                    formik.setFieldTouched('trade_from');
                  }}
                  label={t('trade_data.search_form.input.trade_from')}
                  slotProps={{
                    textField: {
                      error: !!formik.errors.trade_from,
                      size: 'small',
                      fullWidth: true,
                      required: true,
                    },
                  }}
                  views={['month', 'year']}
                />
                {renderError('trade_from')}
              </Box>
              <Box flex={1}>
                <DatePickerWithDefaultIcon
                  disableFuture={true}
                  value={formik.values.trade_to}
                  // disabled={true}
                  minDate={dayjs(runtimeConfig.MIN_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                  maxDate={dayjs(runtimeConfig.MAX_TRADE_DATA_DATE, 'YYYY-MM-DD')}
                  onChange={(data) => {
                    formik.setFieldValue('trade_to', data);
                    formik.setFieldTouched('trade_to');
                  }}
                  label={t('trade_data.search_form.input.trade_to')}
                  slotProps={{
                    textField: {
                      error: !!formik.errors.trade_from,
                      size: 'small',
                      fullWidth: true,
                      required: true,
                    },
                  }}
                  views={['month', 'year']}
                />
                {renderError('trade_to')}
              </Box>
            </Stack>

            {visibleMoreFilter && (
              <React.Fragment>
                <Box>
                  <Stack spacing={2} direction={'row'}>
                    <FormAutoCompleteByCountry
                      id={'export_country'}
                      countryCodeField={'code'}
                      getOptionKeyFn={(option) => `${option['code']}-${option['phone']}`}
                      labelField={'label'}
                      isError={
                        (formik.touched['export_country'] || formik.touched['import_country']) &&
                        Boolean(formik.errors['export_country'])
                      }
                      value={formik.values.export_country}
                      countryCode={formik.values.export_country?.code}
                      placeholder={t('trade_data.search_form.input.export_country')}
                      onBlur={() => {
                        formik.setFieldTouched('export_country');
                      }}
                      onChange={(value: ITradeDataPortInfo) => {
                        // setSupplierSearchKeyword('');
                        formik.setFieldValue('port_of_origin', null);
                        formik.setFieldValue('supplier', null);
                        formik.setFieldValue('export_country', value);
                      }}
                      options={sortObjectsArrayByKey(COUNTRIES, 'label')}
                    />

                    <FormAutoCompleteByCountry
                      id={'import_country'}
                      getOptionKeyFn={(option) => `${option['code']}-${option['phone']}`}
                      labelField={'label'}
                      isError={
                        (formik.touched['export_country'] || formik.touched['import_country']) &&
                        Boolean(formik.errors['export_country'])
                      }
                      countryCodeField={'code'}
                      value={formik.values.import_country}
                      countryCode={formik.values.import_country?.code}
                      placeholder={t('trade_data.search_form.input.import_country')}
                      onBlur={() => {
                        formik.setFieldTouched('import_country');
                      }}
                      onChange={(value: ITradeDataPortInfo) => {
                        // setBuyerSearchKeyword('');
                        formik.setFieldValue('port_of_delivery', null);
                        formik.setFieldValue('buyer', null);
                        formik.setFieldValue('import_country', value);
                      }}
                      options={sortObjectsArrayByKey(COUNTRIES, 'label')}
                      // options={sortObjectsArrayByKey(COUNTRIES, 'label').filter((item) => {
                      //   return runtimeConfig?.LIMITED_BUYERS_COUNTRIES.includes(item.code);
                      // })}
                    />
                  </Stack>
                  {(formik.touched['export_country'] || formik.touched['import_country']) &&
                    formik.errors['export_country'] && (
                      <FormHelperText sx={{ marginLeft: 2 }} error id="standard-weight-helper-text-email-login">
                        {get(formik.errors, 'export_country', '').toString()}
                      </FormHelperText>
                    )}
                </Box>
                <Box>
                  <Stack spacing={2} direction={'row'}>
                    <Box flex={1}>
                      <TextField
                        defaultValue={formik.values.supplier_keyword}
                        label="Keyword (Supplier Name)"
                        fullWidth
                        onChange={debounce(formik.handleChange, 500)}
                        onBlur={formik.handleBlur}
                        size="small"
                        id="keyword"
                        name="supplier_keyword"
                        placeholder="Keyword (Supplier Name)"
                      />
                    </Box>
                    <Box flex={1}>
                      <TextField
                        defaultValue={formik.values.buyer_keyword}
                        onChange={debounce(formik.handleChange, 500)}
                        label="Keyword (Buyer Name)"
                        fullWidth
                        onBlur={formik.handleBlur}
                        size="small"
                        id="keyword"
                        name="buyer_keyword"
                        placeholder="Keyword (Buyer Name)"
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box>
                  <Stack spacing={2} direction={'row'}>
                    {/*<FormAutoCompleteByCountry*/}
                    {/*  id={'port_of_origin'}*/}
                    {/*  labelField={'port_name'}*/}
                    {/*  getOptionKeyFn={(option) => option['port_code']}*/}
                    {/*  countryCode={formik.values.port_of_origin?.country_code}*/}
                    {/*  value={formik.values.port_of_origin}*/}
                    {/*  inputValue={pooSearchKeyword}*/}
                    {/*  placeholder={t('trade_data.search_form.input.origin_port')}*/}
                    {/*  onBlur={() => {*/}
                    {/*    formik.setFieldTouched('port_of_origin');*/}
                    {/*  }}*/}
                    {/*  onTextInputChange={(value: string) => {*/}
                    {/*    setPOOSearchKeyword(value || '');*/}
                    {/*  }}*/}
                    {/*  onChange={(value: ITradeDataPortInfo) => {*/}
                    {/*    formik.setFieldValue('port_of_origin', value);*/}
                    {/*  }}*/}
                    {/*  options={dataPOOResp.data?.data}*/}
                    {/*/>*/}
                    {/*<FormAutoCompleteByCountry*/}
                    {/*  id={'port_of_delivery'}*/}
                    {/*  getOptionKeyFn={(option) => option['port_code']}*/}
                    {/*  labelField={'port_name'}*/}
                    {/*  value={formik.values.port_of_delivery}*/}
                    {/*  countryCode={formik.values.port_of_delivery?.country_code}*/}
                    {/*  inputValue={podSearchKeyword}*/}
                    {/*  placeholder={t('trade_data.search_form.input.delivery_port')}*/}
                    {/*  onBlur={() => {*/}
                    {/*    formik.setFieldTouched('port_of_delivery');*/}
                    {/*  }}*/}
                    {/*  onTextInputChange={(value: string) => {*/}
                    {/*    setPODSearchKeyword(value || '');*/}
                    {/*  }}*/}
                    {/*  onChange={(value: ITradeDataPortInfo) => {*/}
                    {/*    formik.setFieldValue('port_of_delivery', value);*/}
                    {/*  }}*/}
                    {/*  options={dataPODResp.data?.data}*/}
                    {/*/>*/}
                  </Stack>
                  {renderError('port_of_origin')}
                </Box>

                {/*<Stack width={'100%'} spacing={2} direction={'row'}>*/}
                {/*  <Box flex={1}>*/}
                {/*    <TextField*/}
                {/*      fullWidth={true}*/}
                {/*      inputProps={{*/}
                {/*        step: 5,*/}
                {/*        min: 0,*/}
                {/*      }}*/}
                {/*      InputProps={{*/}
                {/*        startAdornment: <InputAdornment position="start">$</InputAdornment>,*/}
                {/*      }}*/}
                {/*      label={t('trade_data.search_form.input.unit_price')}*/}
                {/*      size={'small'}*/}
                {/*      type={'number'}*/}
                {/*    />*/}
                {/*  </Box>*/}
                {/*  <Box flex={1}>*/}
                {/*    <TextField*/}
                {/*      fullWidth={true}*/}
                {/*      inputProps={{*/}
                {/*        step: 5,*/}
                {/*        min: 0,*/}
                {/*      }}*/}
                {/*      InputProps={{*/}
                {/*        startAdornment: <InputAdornment position="start">$</InputAdornment>,*/}
                {/*      }}*/}
                {/*      label={t('trade_data.search_form.input.total_value')}*/}
                {/*      size={'small'}*/}
                {/*      type={'number'}*/}
                {/*    />*/}
                {/*  </Box>*/}
                {/*</Stack>*/}

                {/*<Stack spacing={2} direction={'row'}>*/}
                {/*  <Box flex={1}>*/}
                {/*    <TextField*/}
                {/*      label={t('trade_data.search_form.input.no_of_quantity')}*/}
                {/*      fullWidth={true}*/}
                {/*      type={'number'}*/}
                {/*      size={'small'}*/}
                {/*    />*/}
                {/*  </Box>*/}
                {/*  <Box flex={1}>*/}
                {/*    <TextField*/}
                {/*      label={t('trade_data.search_form.input.no_of_weight')}*/}
                {/*      fullWidth={true}*/}
                {/*      type={'number'}*/}
                {/*      size={'small'}*/}
                {/*    />*/}
                {/*  </Box>*/}
                {/*</Stack>*/}
              </React.Fragment>
            )}

            {renderButtonMoreFilter()}
            {transactionsCountResp.data?.data !== undefined && (
              <Box
                sx={{
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  background: !isTransactionQueryPaid ? theme.palette.info.light : theme.palette.success.light,
                }}
              >
                <Stack flexDirection={'row'} gap={2} alignItems={'center'} justifyContent="center">
                  {!isTransactionQueryPaid ? (
                    <>
                      <CreditCoinIcon sx={{ width: '22px', height: '22px' }} />
                      <Typography variant={'body2'}>
                        We've found{' '}
                        <Typography color={theme.palette.info.main} variant={'body2'} display={'inline'}>
                          {formatNumberWithSuffix(transactionsCountResp.data?.data)} Bills of Lading
                        </Typography>{' '}
                        matching your search criteria.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CheckedCredit sx={{ mb: 0.5 }} fontSize="medium" />
                      <Typography variant={'body2'}>
                        You have access to{' '}
                        <Typography color={theme.palette.success.main} variant={'body2'} display={'inline'}>
                          {renderNumberOrDefault(transactionsCountResp.data?.data, '0')} Bills of Lading
                        </Typography>{' '}
                        <Typography variant={'body2'} mr={0.5} display={'inline'}>
                          matching your search criteria.
                        </Typography>
                        <Tooltip title={'You have access to this data for 30 days'}>
                          <InfoOutlinedIcon
                            fontSize={'small'}
                            sx={{
                              color: theme.palette.text.secondary,
                              verticalAlign: 'middle',
                              mb: 0.5,
                              cursor: 'pointer',
                            }}
                          />
                        </Tooltip>
                      </Typography>
                    </>
                  )}
                </Stack>
                {/* Show explain note when
                      _ Zero records found
                      _ Import and export country selected
                */}
                {transactionsCountResp.data?.data === 0 &&
                  formik.values.export_country?.code &&
                  formik.values.import_country?.code && (
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      While there's no direct bilateral data between these two countries, we do have extensive data
                      to/from 113 other countries, ~80% of global trade for both of these markets. Please encourage your
                      national customs agency to release direct data to enhance the experience of this platform.
                    </Typography>
                  )}
              </Box>
            )}
            <ButtonWithLoading
              sx={{ height: 53 }}
              size="large"
              variant="contained"
              isRounded
              isLoading={some([transactionsCountResp.isFetching, dataHSCodeAIOptionsResp.isFetching])}
              type="submit"
              disabled={transactionsCountResp.data?.data === 0}
              startIcon={<TravelExplore />}
            >
              {!isTransactionQueryPaid || !formik.values.hs_code ? (
                <>
                  <CreditCostIndicator pointData={pointsViewTradeData} />
                  <Typography ml={2} color={'white'}>
                    Unlock Trade Data Now
                  </Typography>
                  <ArrowForwardIcon sx={{ ml: 1 }} fontSize={'small'} />
                </>
              ) : (
                <>
                  <CheckedCredit fontSize="medium" />
                  <Typography ml={2} color={'white'}>
                    Access Trade Data Now
                  </Typography>
                  <ArrowForwardIcon sx={{ ml: 1 }} fontSize={'small'} />
                </>
              )}
            </ButtonWithLoading>
            {transactionsCountResp.isFetching && (
              <Typography align="center" variant="caption2" color="text.secondary">
                Please wait a few seconds while we compile your report.
              </Typography>
            )}
          </Stack>
        </Box>
      </FormikForm>
    </Box>
  );
};

export default FormSearchGlobalTrade;
